import type { CandidatePairStats, PlaycastWebrtcStats, RtpStats, TransportStats } from './typesMedia';
import type { PlaycastWebrtcQualityRawStats, PeerQualityRecommendation } from '@playcastdotio/CoreTypes';
import { isMobileDevice } from './util';
import { collectMetrics } from './sentryStats';

const IS_MOBILE = isMobileDevice();

export const parseStats = async (peerConnection: RTCPeerConnection, peerId: string): Promise<PlaycastWebrtcStats> => {
  const stats = await peerConnection.getStats();

  const transportStats: TransportStats = {
    bytesReceived: 0,
    bytesSent: 0,
    iceState: '',
    selectedCandidatePairId: '',
    packetsSent: 0,
    packetsReceived: 0,
    selectedCandidatePairChanges: 0,
    tlsVersion: '',
  };

  const inboundRtpStats: RtpStats[] = [];
  const outboundRtpStats: RtpStats[] = [];
  let candidatePairStats: CandidatePairStats = {
    nominated: false,
    availableIncomingBitrate: 0,
    availableOutgoingBitrate: 0,
    bytesReceived: 0,
    bytesSent: 0,
    currentRoundTripTime: 0,
    lastPacketReceivedTimestamp: 0,
    lastPacketSentTimestamp: 0,
    requestsReceived: 0,
    requestsSent: 0,
    state: '',
    transportId: '',
  };

  // get transport first as we require the selected candidate pair id
  stats.forEach((report) => {
    if (report.type === 'transport') {
      transportStats.bytesReceived = report.bytesReceived || 0;
      transportStats.bytesSent = report.bytesSent || 0;
      transportStats.iceState = report.iceState || '';
      transportStats.selectedCandidatePairId = report.selectedCandidatePairId || '';
      transportStats.packetsSent = report.packetsSent || 0;
      transportStats.packetsReceived = report.packetsReceived || 0;
      transportStats.selectedCandidatePairChanges = report.selectedCandidatePairChanges || 0;
      transportStats.tlsVersion = report.tlsVersion || '';
    }
  });

  stats.forEach((report) => {
    if (report.type === 'inbound-rtp' || report.type === 'outbound-rtp') {
      // Extract timing data for both inbound and outbound
      const totalEncodeTime = report.totalEncodeTime || 0;
      const totalDecodeTime = report.totalDecodeTime || 0;
      const framesEncoded = report.framesEncoded || 0;
      const framesDecoded = report.framesDecoded || 0;

      // Extract processing delay metrics (inbound-rtp only)
      const totalProcessingDelay = report.totalProcessingDelay || 0;
      const jitterBufferEmittedCount = report.jitterBufferEmittedCount || 0;

      // Calculate average times per frame (convert from seconds to milliseconds)
      const averageEncodeTimePerFrameMs = framesEncoded > 0 ? (totalEncodeTime / framesEncoded) * 1000 : 0;
      const averageDecodeTimePerFrameMs = framesDecoded > 0 ? (totalDecodeTime / framesDecoded) * 1000 : 0;
      const averageProcessingDelayPerFrameMs = jitterBufferEmittedCount > 0 ? (totalProcessingDelay / jitterBufferEmittedCount) * 1000 : 0;

      const rtpStat: RtpStats = {
        type: report.type,
        ssrc: report.ssrc,
        sinkWant: report.sinkWant || '',
        sinkWantAsInt: report.sinkWantAsInt || 0,
        codec: {
          id: report.codecId,
          name: report.codecName || '',
        },
        bytesSent: report.bytesSent || 0,
        packetsSent: report.packetsSent || 0,
        packetsLost: report.packetsLost || 0,
        fractionLost: report.fractionLost || 0,
        audioLevel: report.audioLevel || 0,
        audioDetected: report.audioDetected || 0,
        framesCaptured: report.framesCaptured || 0,
        framesRendered: report.framesRendered || 0,
        voiceActivityDetectorProcessTime: report.voiceActivityDetectorProcessTime || 0,
        passthroughCount: report.passthroughCount || 0,
        encryptSuccessCount: report.encryptSuccessCount || 0,
        encryptFailureCount: report.encryptFailureCount || 0,
        encryptDuration: report.encryptDuration || 0,
        encryptAttempts: report.encryptAttempts || 0,
        encryptMaxAttempts: report.encryptMaxAttempts || 0,
        qualityLimitationReason: report.qualityLimitationReason || '',
        bitrate: report.bitrate || 0,
        framesPerSecond: report.framesPerSecond || 0,
        frameWidth: report.frameWidth || 0,
        frameHeight: report.frameHeight || 0,
        jitter: report.jitter || 0,
        // Add timing data
        totalEncodeTime,
        totalDecodeTime,
        framesEncoded,
        framesDecoded,
        averageEncodeTimePerFrameMs,
        averageDecodeTimePerFrameMs,
        // Add processing delay data
        totalProcessingDelay,
        jitterBufferEmittedCount,
        averageProcessingDelayPerFrameMs,
      };

      if (report.type === 'inbound-rtp') {
        inboundRtpStats.push(rtpStat);
      } else if (report.type === 'outbound-rtp') {
        outboundRtpStats.push(rtpStat);
      }
    }
    if (report.type === 'candidate-pair') {
      if (report.state === 'succeeded' && report.nominated && transportStats.selectedCandidatePairId === report.id) {
        candidatePairStats = {
          nominated: report.nominated || false,
          availableIncomingBitrate: report.availableIncomingBitrate || 0,
          availableOutgoingBitrate: report.availableOutgoingBitrate || 0,
          bytesReceived: report.bytesReceived || 0,
          bytesSent: report.bytesSent || 0,
          currentRoundTripTime: report.currentRoundTripTime || 0,
          lastPacketReceivedTimestamp: report.lastPacketReceivedTimestamp || 0,
          lastPacketSentTimestamp: report.lastPacketSentTimestamp || 0,
          requestsReceived: report.requestsReceived || 0,
          requestsSent: report.requestsSent || 0,
          state: report.state || '',
          transportId: report.transportId || '',
        };
      }
    }
  });

  const quality = getStreamQuality(stats, candidatePairStats, peerId);

  const pcStats = {
    transport: transportStats,
    candidatePair: candidatePairStats,
    rtp: {
      inbound: inboundRtpStats,
      outbound: outboundRtpStats,
    },
    quality,
  };

  collectMetrics(pcStats, peerId);

  return pcStats;
};

export const resetQualityStore = (peerId: string) => {
  qualityStore[peerId] = {
    activeVideoId: '',
    jitterBufferDelay: 0,
    interFrameDelays: [],
    recentFreezes: [],
    recentNacks: [],
    freezeBaseline: 0,
    nackBaseline: 0,
    lastFramesReceived: 0,
    lastTimestamp: 0,
    frameRates: [],
    lastTotalPacketsReceived: 0,
    lastTotalPacketsLost: 0,
    packetLossSamples: [],
    lastBytesReceived: 0,
    bitrates: [],
  };
};

const qualityStore: {
  [peerId: string]: {
    activeVideoId: string;
    jitterBufferDelay: number;
    interFrameDelays: number[];
    recentFreezes: { timestamp: number; count: number }[];
    recentNacks: { timestamp: number; count: number }[];
    freezeBaseline: number;
    lastFramesReceived: number;
    lastTimestamp: number;
    nackBaseline: number;
    frameRates: number[];
    lastTotalPacketsReceived: number;
    lastTotalPacketsLost: number;
    packetLossSamples: number[];
    lastBytesReceived: number;
    bitrates: number[];
  };
} = {};

const getStreamQuality = (
  statsReport: RTCStatsReport,
  candidatePeerStats: CandidatePairStats,
  peerId: string,
): { recommendation: PeerQualityRecommendation; reason: string; rawStats: PlaycastWebrtcQualityRawStats } => {
  let hasActiveVideoTrack = false;
  statsReport.forEach((report) => {
    if (report.type === 'inbound-rtp' && report.kind === 'video') {
      // if it also has a codec assigned, we consider it active
      if (report.codecId) {
        hasActiveVideoTrack = true;
      }
    }
  });

  // If no active video tracks, return maintain recommendation
  if (!hasActiveVideoTrack) {
    // reset our quality store
    qualityStore[peerId] = {
      activeVideoId: '',
      jitterBufferDelay: 0,
      interFrameDelays: [],
      recentFreezes: [],
      recentNacks: [],
      freezeBaseline: 0,
      nackBaseline: 0,
      lastFramesReceived: 0,
      lastTimestamp: 0,
      frameRates: [],
      lastTotalPacketsReceived: 0,
      lastTotalPacketsLost: 0,
      packetLossSamples: [],
      lastBytesReceived: 0,
      bitrates: [],
    };
    return {
      recommendation: 'maintain',
      reason: 'NO_ACTIVE_VIDEO_TRACKS',
      rawStats: {
        hasActiveVideoTrack: false,
        jitterBufferDelay: 0,
        currentRoundTripTime: 0,
        averageDecodeTimePerFrame: 0,
        jitter: 0,
        packetLossPercentage: 0,
        packetSamples: [],
        recentFreezeDelta: 0,
        freezeBaseline: 0,
        recentFreezes: [],
        avgInterFrameDelay: 0,
        nackBaseline: 0,
        recentNackDelta: 0,
        recentNacks: [],
        bitsPerSecond: 0,
        videoKbps: 0,
        avgFrameRate: 0,
        maxRecentFrameRate: 0,
        minRecentFrameRate: 0,
        isFrameRateAboveMinimum: false,
        isFrameRateStable: false,
        isFrameRatePanic: false,
        isBitratePanic: false,
        isMobile: IS_MOBILE,
      },
    };
  }

  const maxFreezeCountWindow = 2000; // Time window in ms to consider for freeze count
  const maxNackCountWindow = 2000; // Time window in ms to consider for NACK count
  const MAX_DECODE_TIME_PER_FRAME = 33.33; // Equivalent to ~30fps
  const MAX_PACKET_LOSS_PERCENTAGE = 2; // n% packet loss
  const MAX_INTER_FRAME_DELAY = 100; // Maximum acceptable inter-frame delay in ms
  const MAX_NACK_COUNT = 35; // Maximum acceptable number of NACKs in the time window
  const MAX_FREEZE_COUNT = 5; // Maximum acceptable number of freezes in the time window

  // Thresholds for increasing quality
  const MIN_DECODE_TIME_PER_FRAME = 20; // We have room to increase if decoding is fast
  const MAX_PACKET_LOSS_PERCENTAGE_FOR_INCREASE = 0.5; // Very low packet loss
  const MIN_INTER_FRAME_DELAY = 50; // Low inter-frame delay indicates room for improvement
  const MAX_NACK_COUNT_FOR_INCREASE = 10; // Low NACK count indicates good network conditions
  const MAX_FREEZE_COUNT_FOR_INCREASE = 2; // Very few freezes indicate stable playback
  const MINIMUM_FRAMERATE = 15;
  const FRAMERATE_DROP_THRESHOLD = 0.25; // 25% drop is considered significant
  const FRAMERATE_HISTORY_SIZE = 7; // Keep track of the last n framerate samples
  const BITRATE_HISTORY_SIZE = 7; // Keep track of the last n bitrate samples
  const PACKET_LOSS_HISTORY_SIZE = 10; // Keep track of the last n packet loss samples
  const MINIMUM_BITRATE = 200 * 1000; // 200kbps
  const TARGET_BITRATE = 1 * 1000 * 1000; // 1mbps

  let totalDecodeTime = 0;
  let totalFramesDecoded = 0;
  let totalPacketsLost = 0;
  let totalPacketsReceived = 0;
  let totalFreezeCount = 0;
  let totalNackCount = 0;
  let timestamp = 0;
  let lastTimestamp = 0;
  let framesReceived = 0;
  let bitsPerSecond = 0;
  let jitter = 0;

  let transients = qualityStore[peerId];
  if (!transients) {
    transients = {
      jitterBufferDelay: 0,
      activeVideoId: '',
      interFrameDelays: [],
      recentFreezes: [],
      recentNacks: [],
      freezeBaseline: 0,
      nackBaseline: 0,
      lastFramesReceived: 0,
      lastTimestamp: 0,
      frameRates: [],
      lastTotalPacketsReceived: 0,
      lastTotalPacketsLost: 0,
      packetLossSamples: [],
      lastBytesReceived: 0,
      bitrates: [],
    };
  }

  // Extract necessary stats from the inbound-rtp report
  statsReport.forEach((report) => {
    if (report.type === 'inbound-rtp' && report.kind === 'video' && report.codecId) {
      if (report.id !== transients.activeVideoId) {
        // Clear our transients if the active video track has changed
        console.warn('[STATS] Active video track has changed, resetting quality store');
        transients = {
          activeVideoId: report.id,
          jitterBufferDelay: 0,
          interFrameDelays: [],
          recentFreezes: [],
          recentNacks: [],
          freezeBaseline: 0,
          nackBaseline: 0,
          lastFramesReceived: 0,
          lastTimestamp: 0,
          frameRates: [],
          lastTotalPacketsReceived: 0,
          lastTotalPacketsLost: 0,
          packetLossSamples: [],
          lastBytesReceived: 0,
          bitrates: [],
        };
      }
      totalDecodeTime = report.totalDecodeTime || 0;
      totalFramesDecoded = report.framesDecoded || 0;
      totalPacketsLost = report.packetsLost || 0;
      totalPacketsReceived = report.packetsReceived || 0;
      totalFreezeCount = report.freezeCount || 0;
      totalNackCount = report.nackCount || 0;
      timestamp = report.timestamp || 0;
      framesReceived = report.framesReceived || 0;
      jitter = report.jitter || 0;

      // Calculate framerate
      if (transients.lastTimestamp > 0) {
        const timeElapsed = (timestamp - transients.lastTimestamp) / 1000; // Convert to seconds
        const framesDelta = framesReceived - transients.lastFramesReceived;
        const currentFrameRate = framesDelta / timeElapsed;
        if (currentFrameRate > 0) {
          transients.frameRates.push(currentFrameRate);
          if (transients.frameRates.length > FRAMERATE_HISTORY_SIZE) {
            transients.frameRates.shift();
          }
        }
      }

      // We want to calculate the jitter buffer delay as [jitterBufferDelay/jitterBufferEmittedCount_in_ms]
      try {
        if (report.type === 'inbound-rtp' && report.kind === 'video') {
          const delay = parseFloat(report['jitterBufferDelay'] as string) || 0;
          const emittedCount = parseFloat(report['jitterBufferEmittedCount'] as string) || 0;
          transients.jitterBufferDelay = emittedCount > 0 ? (delay / emittedCount) * 1000 : 0;
        }
      } catch (error) {
        console.error('[STATS] Error calculating jitter buffer delay', error);
      }

      // Calculate bits/sec
      const bitsReceived = (report.bytesReceived || 0) * 8;
      const bitsDelta = bitsReceived - transients.lastBytesReceived * 8;
      bitsPerSecond = bitsDelta;
      transients.lastBytesReceived = report.bytesReceived || 0;

      transients.lastTimestamp = timestamp;
      transients.lastFramesReceived = framesReceived;
      transients.bitrates.push(bitsPerSecond);
      // ensure we only keep the last n samples
      if (transients.bitrates.length > BITRATE_HISTORY_SIZE) {
        transients.bitrates.shift();
      }

      // Track recent freeze events with timestamps
      if (totalFreezeCount > 0) {
        transients.recentFreezes.push({ timestamp, count: totalFreezeCount });
      }

      while (transients.recentFreezes.length > 0 && timestamp - transients.recentFreezes[0].timestamp > maxFreezeCountWindow) {
        const removedFreeze = transients.recentFreezes.shift();
        if (removedFreeze) {
          transients.freezeBaseline = removedFreeze.count > transients.freezeBaseline ? removedFreeze.count : transients.freezeBaseline;
        }
      }

      // Track recent NACK events with timestamps
      if (totalNackCount > 0) {
        transients.recentNacks.push({ timestamp, count: totalNackCount });
      }

      while (transients.recentNacks.length > 0 && timestamp - transients.recentNacks[0].timestamp > maxNackCountWindow) {
        const removedNack = transients.recentNacks.shift();
        if (removedNack) {
          transients.nackBaseline = removedNack.count > transients.nackBaseline ? removedNack.count : transients.nackBaseline;
        }
      }

      // Calculate the interval between frames for inter-frame delay detection
      if (lastTimestamp > 0) {
        const interFrameDelay = timestamp - lastTimestamp;
        transients.interFrameDelays.push(interFrameDelay);
        if (transients.interFrameDelays.length > FRAMERATE_HISTORY_SIZE) {
          // Keep only the last n samples
          transients.interFrameDelays.shift();
        }
      }

      // Calculate packet loss sample
      let packetLossSample = 0;

      if (transients.lastTotalPacketsReceived > 0 && transients.lastTotalPacketsLost >= 0) {
        // Calculate deltas
        const packetLossDelta = Math.max(0, totalPacketsLost - transients.lastTotalPacketsLost);
        const packetReceivedDelta = Math.max(0, totalPacketsReceived - transients.lastTotalPacketsReceived);

        // Only calculate the percentage if we have received packets
        if (packetReceivedDelta + packetLossDelta > 0) {
          packetLossSample = (packetLossDelta / (packetReceivedDelta + packetLossDelta)) * 100;
        }
      } else {
        // First sample - use current totals instead of deltas
        if (totalPacketsReceived + totalPacketsLost > 0) {
          packetLossSample = (totalPacketsLost / (totalPacketsReceived + totalPacketsLost)) * 100;
        }
      }

      // Ensure the sample is within valid range
      packetLossSample = Math.max(0, Math.min(100, packetLossSample));

      transients.lastTotalPacketsLost = totalPacketsLost;
      transients.lastTotalPacketsReceived = totalPacketsReceived;

      if (packetLossSample >= 0) {
        transients.packetLossSamples.push(packetLossSample);
        if (transients.packetLossSamples.length > PACKET_LOSS_HISTORY_SIZE) {
          transients.packetLossSamples.shift();
        }
      }

      lastTimestamp = timestamp;
    }
  });

  // Calculate decode time per frame
  const averageDecodeTimePerFrame = totalFramesDecoded > 0 ? totalDecodeTime / totalFramesDecoded : 0;

  // Calculate packet loss percentage
  const packetLossPercentage =
    transients.packetLossSamples.length > 0
      ? transients.packetLossSamples.reduce((a, b) => a + b, 0) / transients.packetLossSamples.length
      : 0;

  // Calculate average inter-frame delay
  const avgInterFrameDelay =
    transients.interFrameDelays.length > 0
      ? transients.interFrameDelays.reduce((a, b) => a + b, 0) / transients.interFrameDelays.length
      : 0;

  // Calculate deltas for recent freezes and NACKs within the sliding window
  const recentFreezeHighest = transients.recentFreezes.reduce((max, freeze) => (freeze.count > max ? freeze.count : max), 0);
  const recentFreezeDelta = recentFreezeHighest - transients.freezeBaseline;
  const recentNackHighest = transients.recentNacks.reduce((max, nack) => (nack.count > max ? nack.count : max), 0);
  const recentNackDelta = recentNackHighest - transients.nackBaseline;

  // Calculate average framerate and check stability
  const avgFrameRate =
    transients.frameRates.length > 0 ? transients.frameRates.reduce((a, b) => a + b, 0) / transients.frameRates.length : 0;

  // Framerate panic is when the framerate is below the minimum and STUCK there.
  const isFrameRatePanic =
    transients.frameRates.length >= FRAMERATE_HISTORY_SIZE / 2 && transients.frameRates.every((rate) => rate <= MINIMUM_FRAMERATE / 2);

  const isBitratePanic =
    transients.bitrates.length >= FRAMERATE_HISTORY_SIZE / 2 && transients.bitrates.every((rate) => rate <= MINIMUM_BITRATE);

  // Check for significant framerate drop
  const maxRecentFrameRate = Math.max(...transients.frameRates);
  const minRecentFrameRate = Math.min(...transients.frameRates);
  const hasSignificantFrameRateDrop = (maxRecentFrameRate - minRecentFrameRate) / maxRecentFrameRate > FRAMERATE_DROP_THRESHOLD;

  // Minimum framerate can be calculated by using the samples, and checking that 60% of them are above the minimum
  const isFrameRateAboveMinimum = transients.frameRates.filter((rate) => rate >= MINIMUM_FRAMERATE).length >= FRAMERATE_HISTORY_SIZE * 0.6;
  const isFrameRateStable = !hasSignificantFrameRateDrop;

  const isBitrateAboveTarget = transients.bitrates.filter((rate) => rate >= TARGET_BITRATE).length >= BITRATE_HISTORY_SIZE * 0.6;
  const isBitrateAboveMinimum = transients.bitrates.filter((rate) => rate >= MINIMUM_BITRATE).length >= BITRATE_HISTORY_SIZE * 0.6;

  const rawStats: PlaycastWebrtcQualityRawStats = {
    hasActiveVideoTrack,
    currentRoundTripTime: candidatePeerStats.currentRoundTripTime,
    averageDecodeTimePerFrame,
    jitter,
    jitterBufferDelay: transients.jitterBufferDelay,
    packetLossPercentage,
    packetSamples: transients.packetLossSamples,
    recentFreezeDelta,
    freezeBaseline: transients.freezeBaseline,
    recentFreezes: transients.recentFreezes,
    avgInterFrameDelay,
    nackBaseline: transients.nackBaseline,
    recentNackDelta,
    recentNacks: transients.recentNacks,
    bitsPerSecond,
    videoKbps: bitsPerSecond / 1024,
    avgFrameRate,
    maxRecentFrameRate,
    minRecentFrameRate,
    isFrameRateAboveMinimum,
    isFrameRateStable,
    isFrameRatePanic,
    isBitratePanic,
    isMobile: IS_MOBILE,
  };

  qualityStore[peerId] = transients;

  if (transients.frameRates.length < FRAMERATE_HISTORY_SIZE) {
    return { recommendation: 'maintain', reason: 'INSUFFICIENT_FRAMERATE_SAMPLES', rawStats };
  }

  if (isFrameRatePanic) {
    return { recommendation: 'panic', reason: 'FRAMERATE_PANIC', rawStats };
  }

  if (isBitratePanic) {
    return { recommendation: 'panic', reason: 'BITRATE_PANIC', rawStats };
  }

  if (averageDecodeTimePerFrame > MAX_DECODE_TIME_PER_FRAME) {
    return { recommendation: 'decrease', reason: 'DECODER_OVERRUN', rawStats };
  }

  if (packetLossPercentage > MAX_PACKET_LOSS_PERCENTAGE) {
    return { recommendation: 'decrease', reason: 'PACKET_LOSS', rawStats };
  }

  if (recentFreezeDelta > MAX_FREEZE_COUNT) {
    return { recommendation: 'decrease', reason: 'TOO_MANY_FREEZES', rawStats };
  }

  if (avgInterFrameDelay > MAX_INTER_FRAME_DELAY) {
    return { recommendation: 'decrease', reason: 'HIGH_INTER_FRAME_DELAY', rawStats };
  }

  if (recentNackDelta > MAX_NACK_COUNT) {
    return { recommendation: 'decrease', reason: 'HIGH_NACK_COUNT', rawStats };
  }

  if (!isBitrateAboveTarget) {
    return { recommendation: 'decrease', reason: 'BITRATE_BELOW_TARGET', rawStats };
  }

  if (!isBitrateAboveMinimum) {
    return { recommendation: 'decrease', reason: 'BITRATE_BELOW_MINIMUM', rawStats };
  }

  // if (!isFrameRateAboveMinimum) {
  //   return { recommendation: 'decrease', reason: 'FRAMERATE_BELOW_MINIMUM', rawStats };
  // }

  // if (!isFrameRateStable) {
  //   return { recommendation: 'decrease', reason: 'UNSTABLE_FRAMERATE', rawStats };
  // }

  // Check if we can increase quality
  const canIncrease =
    averageDecodeTimePerFrame < MIN_DECODE_TIME_PER_FRAME &&
    packetLossPercentage < MAX_PACKET_LOSS_PERCENTAGE_FOR_INCREASE &&
    avgInterFrameDelay < MIN_INTER_FRAME_DELAY &&
    recentNackDelta < MAX_NACK_COUNT_FOR_INCREASE &&
    recentFreezeDelta < MAX_FREEZE_COUNT_FOR_INCREASE;
  // &&
  // isFrameRateAboveMinimum &&
  // isFrameRateStable;

  if (canIncrease) {
    return { recommendation: 'increase', reason: 'GOOD_PERFORMANCE', rawStats };
  }

  return { recommendation: 'maintain', reason: '', rawStats };
};
